/**
 * suggestedOrdinalColorMappings.ts
 *
 * Provides some standard mappings from string labels to color hex codes for use in ordinal color scales.
 *
 * !Instructions / Encouragement:
 *
 * - If you find yourself regularly applying the same color to a specific label or type of data,
 *   please add it here. It should help users.
 */
/* oxlint-disable quote-props -- We'll quote them so they are easier to alphabetically sort */

import { ScaleConditionType } from "@/utilities/scale/rules/ruleConstants";
import type { ScaleRuleSet } from "@/utilities/scale/rules/ruleInterfaces";

/**
 * Standard color mappings for ordinal labels.
 */
// oxfmt-ignore
export const standardOrdinalColorMappings: Record<string, string> = {
  // -------------------------------------------------------------------
  // Misc
  // -------------------------------------------------------------------
  other: "#808080", // Gray
  unknown: "#222222", // Dark gray
  missing: "#d3d3d3", // Light gray
  "[]": "#111111", // Almost black
  default: "#808080", // Gray
  null: "#666666", // Gray
  undefined: "#888888", // Gray

  // -------------------------------------------------------------------
  // Water or gas                          (startsWith/endsWith matches)
  // -------------------------------------------------------------------
  water: "#1e90ff", // Blue
  gas: "#ffa500", // Orange

  // -------------------------------------------------------------------
  // Common lithology values
  //         (see: endla/general/models.py > lithology_settings_default)
  // -------------------------------------------------------------------
  // @sort-start
  aquifer: "#00bfff", // Deep Sky Blue
  "carb shale": "#986633", // Brown
  "carb shale_adj": "#986633", // Brown
  "carb shale_isol": "#986633", // Brown
  "carb-shale": "#986633", // Brown
  carb: "#693822", // Dark Brown
  "cemented sand": "#ff33cc", // Pink
  "cemented sandstone": "#ff33cc", // Pink
  "cemented-sand": "#ff33cc", // Pink
  clay: "#bfbfbf", // Light Gray
  "coal-high-quality": "#000000", // Black
  "coal-low-quality": "#222222", // Dark Gray
  coal: "#000000", // Black
  dolomite: "#f9bfbf", // Light Pink
  "gas-rich-carb-shale": "#884628", // Brown
  gassymud: "#6699cc", // Light Blue
  "high-risk-sand": "#bbaf87", // Tan
  interburden: "#fffe00", // Yellow
  mud: "#ffdb67", // Light Yellow
  primary_tuff: "#ff0607", // Red
  sand: "#fffe00", // Yellow
  sandstone: "#ffff00", // Yellow
  shale: "#595959", // Gray
  "silt-shale": "#3f2f27", // Dark Brown
  silt: "#ffd345", // Light Yellow
  siltstone: "#ffffcb", // Pale Yellow
  tuff: "#ff0607", // Red
  // @sort-end

  // -------------------------------------------------------------------
  // Common job status values
  // -------------------------------------------------------------------
  pending: "#d08700", // Yellow
  not_started: "#d08700", // Yellow
  running: "#4dbbf7", // Blue
  success: "#1f893d", // Green
  complete: "#1f893d", // Green
  fail: "#bd260e", // Red
  failed: "#bd260e", // Red
  error: "#bd260e", // Red
  retry: "#d0870033", // Faint Yellow (semi-transparent)

  // -------------------------------------------------------------------
  // Schematic / well parts
  //         (see: endla/general/models.py > schematic_settings_default)
  // -------------------------------------------------------------------
  "blank joints": "#d3d3d3", // Light Gray
  "blank pups": "#add8e6", // Light Blue
  "slotted joints": "#e0bbe4", // Lavender
  "slotted pups": "#ffb6c1", // Light Pink
  special: "#ece6d2", // Beige
  "blank packer pups": "#ade6d3", // Mint Green
  "well background": "#331103b3", // Dark Brown Transparent
  "well hole": "#e7e7e7e6", // Light Gray Transparent
  "target exposure zone": "#0000cd", // Medium Blue
  production: "#ff0000", // Red
  cement: "#546366", // Steel Gray
  locator: "#a52a2a", // Brown
  collar: "#00ffff", // Cyan
  spacer: "#000000", // Black
  nipple: "#ffffff", // White
  shoe: "#a52a2a", // Brown
  casing: "#808080", // Gray
  hanger: "#006400", // Dark Green
  joint: "#800080", // Purple
  "pup joint": "#ffc0cb", // Pink
  sleeve: "#008000", // Green

  // -------------------------------------------------------------------
  // Node types / map markers
  //                 (see: nodeSettingsDefault / schematic node styling)
  // -------------------------------------------------------------------
  // @sort-start
  appraisal: "#ffff00", // Yellow
  "bore pump": "#ff69b4", // Hot Pink
  "brine injection well": "#ff00ff", // Magenta
  "compressor station": "#ffdab9", // Peach
  compressor: "#cab2d6", // Light Purple
  conduit: "#a0522d", // Sienna
  "connector hub": "#000000", // Black
  "degasser outlet": "#6a5acd", // Slate Blue
  "desktop to scouted tip": "#000000", // Black
  development: "#ffff00", // Yellow
  dt_tip: "#000000", // Black
  "dtp to scouted tip": "#000000", // Black
  "end cap": "#8b0000", // Dark Red
  "end point riser": "#0000ff", // Blue
  facility: "#4682b4", // Steel Blue
  "future tip": "#000000", // Black
  "g/w/p split": "#000000", // Black
  installation: "#00ff00", // Bright green
  "isolation valve": "#8b0000", // Dark Red
  manifold: "#8b0000", // Dark Red
  "nodal compressor station": "#800080", // Purple
  "operations centre": "#228b22", // Forest Green
  p_prodfac: "#4682b4", // Steel Blue
  "pulling pit": "#ff6347", // Tomato
  pump: "#1f78b4", // Nice blue
  "reverse osmosis plant": "#ffa500", // Orange
  riser: "#0000ff", // Blue
  "scouted  to desktop tip": "#000000", // Black (double space preserved)
  "scouted to desktop tip": "#000000", // Black
  "scouted to dtp tip": "#000000", // Black
  separator: "#dc5e60", // Light Red
  sink: "#f90202", // Bright Red
  source: "#ffff00", // Yellow
  "start tip": "#000000", // Black
  tank: "#ff0000", // Red
  "tie-in point": "#000000", // Black
  "tie-in-point": "#000000", // Black
  "tip me": "#000000", // Black
  "tip se": "#000000", // Black
  tip: "#000000", // Black
  "truck fill": "#1e90ff", // Dodger Blue
  valve: "#8b0000", // Dark Red
  "water treatment": "#008000", // Green
  "well centre": "#ffff00", // Yellow
  "well pad": "#ffff00", // Yellow
  "well pad_dt": "#ffff00", // Yellow
  well: "#ffff00", // Yellow
  // @sort-end

  // -------------------------------------------------------------------
  // Fittings / inline features
  // -------------------------------------------------------------------
  high_point_vent: "#00df03", // Bright Green
  hpv: "#00df03", // Bright Green
  "high point vent": "#00df03", // Bright Green
  low_point_drain: "#00b3ff", // Bright Blue
  "low point drain": "#00b3ff", // Bright Blue
  lpd: "#00b3ff", // Bright Blue
  "low point drain and reinjection riser": "#00b3ff", // Bright Blue
};

/**
 * Returns a mapped color for the given label using standardOrdinalColorMappings.
 * Matching order:
 *   1. An exact match
 *   2. A startsWith / endsWith match (if enabled)
 *
 * @param label - The input string label to look up (e.g. "Running", "Coal-Shale").
 * @param ignoreCase - Whether to ignore case when matching (default: true).
 * @param alsoCheckStartsWith - Whether to check for startsWith matches (default: true).
 * @param alsoCheckEndsWith - Whether to check for endsWith matches (default: true).
 * @param fallbackColor - Optional fallback color if no match is found.
 * @returns A color string (hex) or fallback/null.
 */
export function getSuggestedMappedColorForOrdinal(
  label: string | undefined | null,
  ignoreCase: boolean = true,
  alsoCheckStartsWith: boolean = false,
  alsoCheckEndsWith: boolean = false,
  fallbackColor: string | null = null
): string | null {
  if (!label) return fallbackColor;

  const normalized = ignoreCase ? label.toLowerCase().trim() : label.trim();

  // First: Exact match
  if (standardOrdinalColorMappings[normalized]) {
    return standardOrdinalColorMappings[normalized];
  }

  // Second: Match by startsWith and/or endsWith
  if (alsoCheckStartsWith) {
    for (const key in standardOrdinalColorMappings) {
      if (Object.prototype.hasOwnProperty.call(standardOrdinalColorMappings, key)) {
        const color = standardOrdinalColorMappings[key];
        if (normalized.startsWith(key)) {
          return color;
        }
      }
    }
  }
  if (alsoCheckEndsWith) {
    for (const key in standardOrdinalColorMappings) {
      if (Object.prototype.hasOwnProperty.call(standardOrdinalColorMappings, key)) {
        const color = standardOrdinalColorMappings[key];
        if (normalized.endsWith(key)) {
          return color;
        }
      }
    }
  }

  // If no match found, return fallback color or null
  return fallbackColor;
}

/**
 * Returns a new rule set with suggested colors applied to constant string equals rules.
 *
 * @param ruleSet - The original rule set to transform.
 * @param alsoCheckStartsWith - Whether to check for startsWith matches in conditions.
 * @param alsoCheckEndsWith - Whether to check for endsWith matches in conditions.
 * @returns An object containing the updated rule set, the number of modified rules, and the number of already matched rules.
 */
export function applySuggestedColorMappedRuleSet(
  ruleSet: ScaleRuleSet,
  alsoCheckStartsWith: boolean = true,
  alsoCheckEndsWith: boolean = true
): {
  newRuleSet: ScaleRuleSet;
  numMatched: number;
  numCandidates: number;
  alreadyMatched: number;
} {
  let numMatched = 0;
  let numCandidates = 0;
  let alreadyMatched = 0;

  const newRules = ruleSet.rules.map((rule) => {
    const isStringEquals = rule.conditions?.some(
      (cond) => cond.type === ScaleConditionType.StringEquals
    );
    const isConstantColor =
      rule.output.outputType === "constant" && typeof rule.output.outputValues?.[0] === "string";

    if (isStringEquals && isConstantColor) {
      let foundColor: string | null = null;
      numCandidates += 1;

      for (const cond of rule.conditions) {
        if (cond.type === ScaleConditionType.StringEquals && cond.inputValues) {
          for (const input of cond.inputValues) {
            const color = getSuggestedMappedColorForOrdinal(
              input.value,
              true, // ignoreCase
              alsoCheckStartsWith,
              alsoCheckEndsWith
            );
            if (color) {
              foundColor = color;
              break;
            }
          }
        }
      }

      if (foundColor) {
        if (foundColor !== rule.output.outputValues[0]) {
          numMatched += 1;
          return {
            ...rule,
            output: {
              ...rule.output,
              outputValues: [foundColor],
            },
          };
        }
        alreadyMatched += 1;
      }
    }

    return rule;
  });

  return {
    newRuleSet: { ...ruleSet, rules: newRules },
    numMatched,
    numCandidates,
    alreadyMatched,
  };
}
